import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Select } from 'antd';
import debounce from 'lodash/debounce';
import {
  fetchEventIdsAndNamesService,
  fetchEventById,
} from '../../../../../features/events-and-classes/api';

const EventsAndClassesIdSelector = ({
  isEventIdRequired,
  eventIdSelectionRef,
  eventId,
  setEventId,
}) => {
  const { Option } = Select;
  const [eventData, setEventData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const pageRef = useRef(0);
  const debounceTimeout = 800;

  const transformEventData = event => {
    return {
      value: event.eventId,
      name: event.name,
      display: `[ID: ${event.eventId}] ${event.name}`,
    };
  };

  const fetchEventData = async searchText => {
    setLoading(true);

    //When searching, we need to reset the dropdown to only show the searched values
    const currentValues = searchText ? [] : eventData;

    if (searchText) {
      setEventData([]);
      pageRef.current = 0;
      setEventId(null);
    }

    const eventsandclassesData = await fetchEventIdsAndNamesService({
      page: pageRef.current,
      searchValue: searchText || searchValue,
    });

    const normalizedEventsAndClassesData =
      eventsandclassesData.map(transformEventData);

    const sortedEventsAndClassesData = [
      ...currentValues, //Will be empty if searching
      ...normalizedEventsAndClassesData,
    ].sort((a, b) => a.name.localeCompare(b.name));

    setEventData(sortedEventsAndClassesData);
    setLoading(false);
    pageRef.current += 1;
  };

  const handleSearch = useMemo(() => {
    const fetchData = async value => {
      setSearchValue(value);
      await fetchEventData(value);
    };

    return debounce(fetchData, debounceTimeout);
  }, [fetchEventData, debounceTimeout]);

  const handleScroll = async ({ target }) => {
    if (
      !loading &&
      // doing this trick with '+ 2' to trigger scrolling when mouse is close to the end of data in dropdown select
      target.scrollTop + target.offsetHeight + 2 > target.scrollHeight
    ) {
      target.scrollTo(0, target.scrollHeight);
      fetchEventData();
    }
  };

  useEffect(() => {
    if (eventId) {
      const fetchByEventId = async () => {
        const event = await fetchEventById(eventId);
        const normalizedEventData = transformEventData(event);

        setEventData([normalizedEventData]);
      };

      fetchByEventId();
    }
  }, []);

  useEffect(() => {
    if (isEventIdRequired) {
      fetchEventData();
    }
  }, [isEventIdRequired]);

  return (
    <Select
      ref={eventIdSelectionRef}
      placeholder="Event/Class ID"
      filterOption={false}
      showSearch
      onSearch={handleSearch}
      onPopupScroll={handleScroll}
      loading={loading}
      value={eventId}
      style={{ width: 180 }}
      onChange={eventId => setEventId(eventId)}
      dropdownMatchSelectWidth={false}
      allowClear
    >
      {eventData.map(({ value, display }) => {
        return (
          <Option key={value} value={value}>
            {display}
          </Option>
        );
      })}
    </Select>
  );
};

export default EventsAndClassesIdSelector;
